<template>
  <div class="agents-list-card-wrapper">
    <div
      class="agents-list-card-header"
      :style="{ backgroundColor: situationColor }"
    >
      <span>{{ situationLabel }}</span>
    </div>

    <div class="agents-list-card">
      <div class="agents-list-card-top">
        <div class="agents-list-card-title">
          <strong>{{ displayName }}</strong>
          <span v-if="agent.nickname" class="agents-list-card-nickname">
            ({{ agent.nickname.trim() }})
          </span>
        </div>

        <div class="agents-list-card-actions">
          <button
            v-if="canEdit"
            type="button"
            class="agents-list-card-action"
            title="Editar"
            @click.stop="$emit('edit', agent)"
          >
            <i class="fas fa-pen"></i>
          </button>
          <button
            v-if="agent.hasAfiliation"
            type="button"
            class="agents-list-card-action"
            title="Ver carteira"
            @click.stop="$emit('view', agent.uuid)"
          >
            <i class="no no-view"></i>
          </button>
        </div>
      </div>

      <div v-if="placeName" class="agents-list-card-row">
        <i class="no no-user"></i>
        <span>{{ placeName }}</span>
      </div>

      <div class="agents-list-card-row">
        <i class="no no-present-3"></i>
        <span>{{ birthdayLabel }}</span>
      </div>

      <div class="agents-list-card-row">
        <i class="no no-translation-3"></i>
        <span>{{ languagesLabel }}</span>
      </div>

      <div class="agents-list-card-row">
        <i class="no no-drop-3"></i>
        <span>{{ blodtypeLabel }}</span>
      </div>

      <div class="agents-list-card-row">
        <i class="no no-position-marker"></i>
        <span>{{ cityLabel }}</span>
      </div>

      <div class="agents-list-card-row">
        <i class="no no-whatsapp"></i>
        <a v-if="whatsappHref" :href="whatsappHref" @click.stop>
          {{ mobileLabel }}
        </a>
        <a v-else-if="mobileLabel !== 'não informado'" :href="`tel:${mobileLabel}`" @click.stop>
          {{ mobileLabel }}
        </a>
        <span v-else>{{ mobileLabel }}</span>
      </div>

      <div class="agents-list-card-row">
        <strong>CNH:</strong>
        <span>{{ cnhLabel }}</span>
      </div>

      <div class="agents-list-card-formations">
        <strong>Cursos e Habilidades:</strong>
        <ul v-if="formationItems.length">
          <li v-for="(formation, idx) in formationItems" :key="idx">
            {{ formation }}
          </li>
        </ul>
        <span v-else class="text-muted">não informado</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgentsListCard',

  props: {
    agent: {
      type: Object,
      required: true,
    },
    situationLabel: {
      type: String,
      default: '--',
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    displayName() {
      return this.agent.name || '--'
    },

    placeName() {
      return this.agent.place_name || ''
    },

    birthdayLabel() {
      const raw =
        this.agent.birthday ||
        (this.agent.Birthday && this.agent.Birthday.value) ||
        ''
      if (!raw) return 'não informado'
      return this.formatBirthday(raw) || 'não informado'
    },

    languagesLabel() {
      const languages = this.agent.languages
      if (!languages) return 'não informado'
      if (Array.isArray(languages)) {
        const labels = languages
          .map((lang) => lang.label || lang)
          .filter(Boolean)
        return labels.length ? labels.join(', ') : 'nenhum'
      }
      return String(languages)
    },

    blodtypeLabel() {
      const blodtype = this.agent.blodtype
      if (!blodtype) return 'não informado'
      if (typeof blodtype === 'object') {
        return (
          (blodtype.value && blodtype.value.value) ||
          blodtype.value ||
          blodtype.label ||
          'não informado'
        )
      }
      return String(blodtype)
    },

    cityLabel() {
      if (this.agent.address_city) {
        return this.agent.address_city.value || this.agent.address_city
      }
      const city = this.agent.City || this.agent.city
      if (!city || !city.name) return 'não informado'
      const state =
        city.State && city.State.namecode ? ` - ${city.State.namecode}` : ''
      return `${city.name}${state}`
    },

    mobileLabel() {
      const mobile = this.agent.mobile
      if (!mobile) return 'não informado'
      if (typeof mobile === 'object') {
        return mobile.value || 'não informado'
      }
      return String(mobile)
    },

    whatsappHref() {
      const mobile = this.agent.mobile
      if (mobile && typeof mobile === 'object' && mobile.whatsapp) {
        return mobile.whatsapp
      }
      return ''
    },

    cnhLabel() {
      const cnh = this.agent.cnh
      if (!cnh) return 'não informado'
      if (typeof cnh === 'object') {
        if (cnh.value && typeof cnh.value === 'object') {
          return cnh.value.label || cnh.value.value || 'não possui'
        }
        return cnh.value || cnh.label || 'não informado'
      }
      return String(cnh)
    },

    formationItems() {
      const formations =
        this.agent.AgentsFormations || this.agent.formations || []
      if (!Array.isArray(formations) || !formations.length) return []

      const unique = {}
      formations.forEach((item) => {
        const formation = item.Formation || item
        if (!formation || !formation.name) return
        const key = String(formation.name)
          .toUpperCase()
          .replace(/[^A-Z]+/g, '')
        const date = formation.date || ''
        if (!unique[key] || date > (unique[key].date || '')) {
          unique[key] = {
            name: formation.name,
            date: date,
          }
        }
      })

      return Object.values(unique).map((formation) => {
        const year = formation.date ? String(formation.date).substring(0, 4) : ''
        return year ? `${formation.name} | ${year}` : formation.name
      })
    },

    situationColor() {
      const label = String(this.situationLabel || '').toLowerCase()
      if (
        label.includes('desabilitado') ||
        label.includes('inativo') ||
        label.includes('desvinculado')
      ) {
        return '#6c757d'
      }
      if (label.includes('novo') || label.includes('aguardando')) {
        return '#e67e22'
      }
      if (label.includes('habilitado') || label === 'ativo') {
        return '#20824f'
      }
      return '#12576e'
    },
  },

  methods: {
    formatBirthday(strBirthDate) {
      let birthDate = new Date(`${strBirthDate} 12:00:00`)
      if (isNaN(birthDate.getTime()) && String(strBirthDate).indexOf('/') > -1) {
        const parts = String(strBirthDate).split('/')
        birthDate = new Date(parts[2], parts[1] / 1 - 1, parts[0])
      }
      if (isNaN(birthDate.getTime())) return ''
      const day = ('0' + birthDate.getDate()).slice(-2)
      const month = birthDate.toLocaleString('default', { month: 'long' })
      return `${day} de ${month}`
    },
  },
}
</script>

<style>
.agents-list-card-wrapper {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e6eef2;
  border-radius: 0.75rem;
  overflow: hidden;
  margin-bottom: 0.85rem;
}

.agents-list-card-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: 28px;
  padding: 0 1rem;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  text-transform: lowercase;
}

.agents-list-card {
  padding: 12px 14px;
}

.agents-list-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
}

.agents-list-card-title {
  color: #1f2a37;
  font-size: .9rem;
  line-height: 1.35;
}

.agents-list-card-nickname {
  color: #5f6b7a;
  font-weight: 400;
}

.agents-list-card-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
}

.agents-list-card-action {
  border: 1px solid #12576e;
  background: #fff;
  color: #12576e;
  width: 34px;
  height: 34px;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.agents-list-card-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #3b4654;
  font-size: 14px;
  margin-bottom: 0.4rem;
}

.agents-list-card-row .no {
  color: #1f2a37;
  min-width: 1.1rem;
}

.agents-list-card-formations {
  margin-top: 0.5rem;
  color: #3b4654;
  font-size: 14px;
}

.agents-list-card-formations ul {
  margin: 0.35rem 0 0;
  padding-left: 1.1rem;
}
</style>
